import * as React from 'react'
import {Column, Filter, FinalState, RowInfo, SortingRule} from 'react-table'
import ReadOnlyView from '../../components/FeatureViews/ReadOnlyView'
import {nls} from '../../components/nls'
import FeaturesTable from '../../components/Tables/FeaturessTable'
import Loading from '../../components/Tables/Loading'
import Pagination from '../../components/Tables/Pagination'
import {DrawSource} from '../../enums/DrawSource'
import {GeometryType} from '../../enums/GeometryType'
import {getFieldValue} from '../../helpers'
import {IFeatureTablesDispatcher, IMapDispatcher, ISingleViewDispatcher} from '../../interfaces/dispatchers'
import {IFeatureLayerInfo, IQuery, ITableProps} from '../../interfaces/models'
import {IColumn} from '../../interfaces/reducers'
import {Feature, FeatureStatus} from '../../models'
import {getMapService} from '../../reducers/map'
import GroupCountColumnCell from './GroupCountColumnCell'
import ReduxAsyncPagination from './ReduxAsyncPagination'
export class PropsProvider {
  protected getGroupColumns(featureLayerInfo: IFeatureLayerInfo, query: IQuery): Array<Column> {
    if (query.returnDistinctValues && (!query.outFields || query.outFields.length != 1)) {
      throw new Error('Query with returnDistinctValues should contain only one outField')
    }
    const groupField = query.outFields![0]
    const columns: Array<Column> = [
      {
        expander: true,
        show: false
      } as any
    ]
    if (featureLayerInfo.fields[groupField].codedValues) {
      columns.push({
        id: featureLayerInfo.fields[groupField].fieldName,
        Header: featureLayerInfo.fields[groupField].label,
        accessor: (d: Feature) => d.getProperties()[featureLayerInfo.fields[groupField].fieldName],
        Cell: cellInfo => (
          <div>
            {getFieldValue(
              featureLayerInfo.fields[featureLayerInfo.fields[groupField].fieldName],
              cellInfo.original.getProperties()[featureLayerInfo.fields[groupField].fieldName]
            )}
          </div>
        )
      })
    } else {
      columns.push({
        id: featureLayerInfo.fields[groupField].fieldName,
        Header: featureLayerInfo.fields[groupField].label,
        accessor: (d: Feature) =>
          getFieldValue(
            featureLayerInfo.fields[featureLayerInfo.fields[groupField].fieldName],
            d.getProperties()[featureLayerInfo.fields[groupField].fieldName]
          )
      })
    }
    columns.push({
      id: 'count',
      Header: 'Брой',
      accessor: (d: Feature) => (
        <GroupCountColumnCell
          layerInfo={featureLayerInfo}
          groupField={groupField}
          query={this.getGroupWhere(featureLayerInfo, query, d)}
        />
      ),
      sortable: false
    })
    return columns
  }

  protected getDetailComponent(featureLayerInfo: IFeatureLayerInfo): (row: RowInfo) => JSX.Element {
    return (row: RowInfo) => {
      return <ReadOnlyView feature={row.original} layerInfo={featureLayerInfo} />
    }
  }

  protected getGroupWhere(featureLayerInfo: IFeatureLayerInfo, query: IQuery, feature: Feature): IQuery {
    const groupQuery = {...query}
    groupQuery.returnDistinctValues = false
    if (groupQuery.returnDistinctValues && (!groupQuery.outFields || groupQuery.outFields.length != 1)) {
      throw new Error('Query with returnDistinctValues should contain only one outField')
    }
    const groupField = groupQuery.outFields![0]
    let where = ''
    const type = featureLayerInfo.fields[groupField].type
    if (type == 'string') {
      where = `${groupField} = '${feature.getProperties()[groupField]}'`
    } else if (type == 'date') {
      where = `${groupField} = timestamp ${feature.getProperties()[groupField]}`
    } else {
      where = `${groupField} = ${feature.getProperties()[groupField]}`
    }
    if (groupQuery.where) {
      groupQuery.where = groupQuery.where + ' AND ' + where
      return groupQuery
    }

    groupQuery.where = where
    return groupQuery
  }

  public getTableProps(
    featureLayerInfo: IFeatureLayerInfo,
    setTableInfoSelections: (tableId: string, selectedIds: any, add: boolean) => void,
    setTableInfoExpanded: (tableId: string, expandedInput: {[id: string]: any}, add?: boolean) => void,
    toggleSelectAll: (tableId: string) => void,
    zoomAll: (tableId: string) => void,
    query: IQuery,
    onFeatureSelect?: (feature?: Feature) => void,
    mapDispatcher?: IMapDispatcher
  ): ITableProps {
    if (getMapService()) {
      getMapService().drawing.unMark(DrawSource.Select, [this.latestFlashFeature])
    }

    if (query.returnDistinctValues && (!query.outFields || query.outFields.length != 1)) {
      throw new Error('Query with returnDistinctValues should contain only one outField')
    }
    const getQuery = (feature: Feature): IQuery => {
      if (query.returnDistinctValues) {
        const groupQuery = {...this.getGroupWhere(featureLayerInfo, query, feature), outFields: ['*']}
        return groupQuery
      }
      return query
    }
    const subComponent = query.returnDistinctValues
      ? this.getNonReduxTable(featureLayerInfo, getQuery, false, this)
      : this.getDetailComponent(featureLayerInfo)
    const columns = this.getColumns(
      featureLayerInfo,
      setTableInfoSelections,
      toggleSelectAll,
      zoomAll,
      query,
      mapDispatcher
    )

    return {
      query: query,
      groupField: query.returnDistinctValues ? query.outFields![0] : '',
      id: featureLayerInfo.url,
      idColumnName: featureLayerInfo.sourceDescription.idField,
      ...this.getDefaultProps(),
      getTdProps: this.getTdProps(featureLayerInfo.url, setTableInfoExpanded, onFeatureSelect),
      getTrProps: this.getTrProps(),
      SubComponent: onFeatureSelect ? undefined : subComponent,
      getTheadThProps: this.getTheadThProps(),
      columns: columns,
      title: featureLayerInfo.sourceDescription.sourceTitle,
      PaginationComponent: (props: ITableProps) => <Pagination {...props} tools={[]} />,
      LoadingComponent: (props: ITableProps) => <Loading {...props} />
    }
  }

  public getReduxTableProps(
    featureLayerInfo: IFeatureLayerInfo,
    dispatchers: IFeatureTablesDispatcher & IMapDispatcher & ISingleViewDispatcher
  ): ITableProps {
    const onFeatureSelect = (feature: Feature) => {
      dispatchers.setSelectedFeature(feature, featureLayerInfo.url)
    }
    const props = this.getTableProps(
      featureLayerInfo,
      dispatchers.setTableInfoSelections,
      dispatchers.setTableInfoExpanded,
      dispatchers.toggleSelectAll,
      dispatchers.zoomAll,
      {where: featureLayerInfo.sourceDescription.constantSqlWhere}, //redux table queries are managed elsewhere - in the dataQueries store
      onFeatureSelect,
      dispatchers
    )
    props.columns![0].show = false
    return props
  }

  public convertToReduxAsync(tableProps: ITableProps, featureTableDispatcher: IFeatureTablesDispatcher): ITableProps {
    const onSortedChange: (newSorted: SortingRule[], column: any, additive: boolean) => void = (
      newSorted,
      column,
      additive
    ) => {
      featureTableDispatcher.setSorting(tableProps.id, newSorted as any)
    }
    tableProps = {...tableProps, page: 0, sorted: undefined, filtered: undefined}
    tableProps.asyncProps = {
      page: tableProps.page || 0,
      pages: tableProps.pages || 0,
      recordsCount: 0
    }
    tableProps.PaginationComponent = () => (
      <ReduxAsyncPagination tableId={tableProps.id} tools={[]} featureTableDispatcher={featureTableDispatcher} />
    )
    tableProps.manual = true
    tableProps.onSortedChange = onSortedChange
    return tableProps
  }

  public getNonReduxTable(
    featureLayerInfo: IFeatureLayerInfo,
    getQuery: (feature: Feature) => IQuery,
    sync: boolean,
    propsProvider: PropsProvider
  ): (row: RowInfo) => JSX.Element {
    return (row: RowInfo) => {
      return (
        <div style={{padding: '12px', backgroundImage: 'linear-gradient(white, rgb(35, 96, 146))'}}>
          <div style={{position: 'relative', minHeight: '400px', backgroundColor: 'white'}}>
            <FeaturesTable
              layerInfo={featureLayerInfo}
              sync={sync}
              query={getQuery(row.original)}
              pageSize={5}
              propsProvider={propsProvider}
            />
          </div>
        </div>
      )
    }
  }

  protected getColumns(
    featureLayerInfo: IFeatureLayerInfo,
    setTableInfoSelections: (tableId: string, selectedIds: any, add: boolean) => void,
    toggleSelectAll: (tableId: string) => void,
    zoomAll: (tableId: string) => void,
    query: IQuery,
    mapDispatcher?: IMapDispatcher
  ): Column[] {
    return query.returnDistinctValues
      ? [...this.getGroupColumns(featureLayerInfo, query)]
      : [
          this.getSelectingColumn(setTableInfoSelections, toggleSelectAll),
          ...this.getFeatureTableColumns(featureLayerInfo)
        ].concat(
          featureLayerInfo.getGeometryType() != GeometryType.None
            ? [this.getZoomToButton(featureLayerInfo, zoomAll, mapDispatcher)]
            : []
        )
  }
  protected setSelected(
    entity: Feature,
    props: Partial<ITableProps>,
    setTableInfoSelections: (tableId: string, selectedIds: any, add: boolean) => void
  ) {
    if (props.selectedIds) {
      const selected = props.selectedIds[entity.getProperties()[props.idColumnName!]!]
      if (selected) {
        setTableInfoSelections(props.id!, {[entity.getProperties()[props.idColumnName!]!]: entity}, false)
      } else {
        setTableInfoSelections(props.id!, {[entity.getProperties()[props.idColumnName!]!]: entity}, true)
      }
    }
  }

  protected getFeatureTableColumns(featureLayerInfo: IFeatureLayerInfo): Array<Column> {
    const columns: Array<Column> = [
      {
        expander: true,
        show: false
      } as any
    ]
    let fieldNames = featureLayerInfo.sourceDescription.tableFields
    if (!fieldNames) {
      fieldNames = []
      for (const name in featureLayerInfo.fields) {
        if (name != 'geometry') fieldNames.push(name)
      }
    }
    fieldNames!.forEach(fieldName => {
      if (featureLayerInfo.fields[fieldName]) {
        if (featureLayerInfo.fields[fieldName].codedValues) {
          columns.push({
            id: featureLayerInfo.fields[fieldName].fieldName,
            Header: featureLayerInfo.fields[fieldName].label,
            accessor: (d: Feature) =>
              getFieldValue(
                featureLayerInfo.fields[featureLayerInfo.fields[fieldName].fieldName],
                d.getProperties()[featureLayerInfo.fields[fieldName].fieldName]
              ),
            Cell: cellInfo => (
              <div>
                {getFieldValue(
                  featureLayerInfo.fields[featureLayerInfo.fields[fieldName].fieldName],
                  cellInfo.original.getProperties()[featureLayerInfo.fields[fieldName].fieldName]
                )}
              </div>
            )
          })
        } else {
          columns.push({
            id: featureLayerInfo.fields[fieldName].fieldName,
            Header: featureLayerInfo.fields[fieldName].label,
            accessor: (d: Feature) =>
              getFieldValue(
                featureLayerInfo.fields[featureLayerInfo.fields[fieldName].fieldName],
                d.getProperties()[featureLayerInfo.fields[fieldName].fieldName]
              )
          })
        }
      }
    })
    return columns
  }

  protected getZoomToButton(
    featureLayerInfo: IFeatureLayerInfo,
    zoomAll: (tableId: string) => void,
    mapDispatcher?: IMapDispatcher
  ): IColumn {
    return {
      Header: (
        <div
          style={{
            fontSize: '25px',
            color: '#407aab',
            textAlign: 'center',
            width: '100%',
            cursor: 'pointer'
          }}
          onClick={() => zoomAll(featureLayerInfo.url)}
        >
          <i className="fas fa-map-marker-alt" />
        </div>
      ),
      id: '$zoom',
      accessor: 'Zoom',
      Cell: this.getZoomControl(mapDispatcher),
      width: 35,
      filterable: false,
      sortable: false,
      resizable: false
    }
  }

  protected getTheadThProps() {
    return (state: FinalState, rowInfo: any, column: any, instance: any) => {
      if (column && column.id && column.id.indexOf('$') == 0) {
        if (instance.props) {
          column.props = instance.props
        }
        return {
          style: {
            padding: '3px'
          }
        }
      }
      return {}
    }
  }

  protected getSelectingColumn(
    setTableInfoSelections: (tableId: string, selectedIds: any, add: boolean) => void,
    toggleSelectAll: (tableId: string) => void
  ): Column {
    return {
      Header: (cellInfo: any) => this.getHeaderSelectControl(cellInfo.column.props, toggleSelectAll),
      id: '$select',
      accessor: 'Select',
      Cell: cellInfo => this.getSelectControl(cellInfo, setTableInfoSelections),
      width: 30,
      filterable: false,
      sortable: false,
      resizable: false
    }
  }

  protected getDefaultProps(): Partial<ITableProps> {
    return {
      pageSize: 25,
      collapseOnSortingChange: true,
      collapseOnPageChange: true,
      collapseOnDataChange: true,
      noDataText: nls.table.noData,
      pageText: '',
      defaultFilterMethod: (filter: Filter, row: any, column: any) => {
        if (row[column.id] && filter.value) {
          return (row[column.id] as number).toString().toUpperCase().indexOf(filter.value.toUpperCase()) == 0
        }
        return filter.value!
      },
      ofText: nls.table.from,
      nextText: '>',
      previousText: '<',
      selectedIds: {},
      onExpandedChange: (column: any, event: any, isTouch: boolean) => {},
      className: '-striped -highlight',
      style: {height: '100%'}
    }
  }

  protected getZoomControl(mapDispatcher?: IMapDispatcher): (cellInfo: any) => JSX.Element {
    return (cellInfo: any) => {
      const checked =
        cellInfo.column.props.selectedIds &&
        cellInfo.column.props.selectedIds[cellInfo.original.getProperties()[cellInfo.column.props.idColumnName!]]
      return (
        <div
          style={{
            opacity: cellInfo.original.getGeometry() ? 1 : 0.5,
            cursor: cellInfo.original.getGeometry() ? 'pointer' : '',
            fontSize: '26px',
            color: '#407aab',
            textAlign: 'center',
            width: '100%'
          }}
          className={checked && 'table-row-tool'}
          onClick={() => {
            if (cellInfo.original.getGeometry()) {
              if (mapDispatcher) {
                this.showPopup(mapDispatcher, cellInfo.original, cellInfo.column.props)
              }
              this.panTo(cellInfo.original)
            }
          }}
          onDoubleClick={() => {
            if (cellInfo.original.getGeometry()) {
              const view = getMapService()
              if (mapDispatcher) {
                view.zoomTo([cellInfo.original.getGeometry()]).then(() => {
                  this.showPopup(mapDispatcher, cellInfo.original, cellInfo.column.props)
                })
              } else {
                view.zoomTo([cellInfo.original.getGeometry()])
              }
            }
          }}
        >
          <i className="fas fa-map-marker-alt" />
        </div>
      )
    }
  }

  protected getSelectControl(
    cellInfo: any,
    setTableInfoSelections: (tableId: string, selectedIds: any, add: boolean) => void
  ): JSX.Element {
    const selected =
      cellInfo.column.props.selectedIds[cellInfo.original.getProperties()[cellInfo.column.props.idColumnName!]] || false
    return (
      <input
        style={{height: '19px', width: '19px'}}
        className={selected ? '' : 'table-row-tool'}
        type="checkbox"
        checked={selected}
        onChange={() => {
          this.setSelected(cellInfo.original, cellInfo.column.props, setTableInfoSelections)
        }}
      />
    )
  }

  protected getHeaderSelectControl(
    props: Partial<ITableProps>,
    toggleSelectAll: (tableId: string) => void
  ): JSX.Element {
    return (
      <input
        style={{height: '19px', width: '19px'}}
        type="checkbox"
        checked={
          !!props && props.recordsCount == (props.selectedIds ? Object.keys(props.selectedIds).length : undefined)
        }
        onChange={() => {
          toggleSelectAll(props.id!)
        }}
      />
    )
  }

  protected getTrProps() {
    return (state: FinalState, rowInfo: RowInfo, column: any, instance: any) => {
      if (rowInfo) {
        const feat = rowInfo.original as Feature
        if (feat && feat.status == FeatureStatus.Deleted) {
          return {
            style: {
              backgroundColor: '#ff000078'
            }
          }
        }
        const expanded = state.expanded ? state.expanded : {}
        const index = rowInfo ? rowInfo.viewIndex : ''
        if ((expanded as any)[index]) {
          return {
            style: {
              backgroundImage: 'linear-gradient(rgb(7, 29, 48), rgb(24, 61, 92) 35%)',
              color: 'white'
            }
          }
        }
      }

      return {}
    }
  }
  latestFlashFeature: Feature
  protected getTdProps(
    tableId: string,
    setTableInfoExpanded: (tableId: string, expanded: any, add?: boolean) => void,
    onFeatureSelect?: (feature?: Feature) => void
  ) {
    return (state: FinalState, rowInfo: RowInfo, column: any, instance: any) => {
      const mapService = getMapService()
      let result = {} as React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLDivElement>
      if (column && instance.props) {
        column.props = instance.props
      }
      if (!column.id) {
        return result
      }
      if (column.id.indexOf('$') != 0) {
        result = {
          onClick: (e: any) => {
            if (onFeatureSelect && rowInfo) {
              const feat = rowInfo.original as Feature
              if (feat.status != FeatureStatus.Deleted) {
                onFeatureSelect(feat)
              }
            } else {
              const expanded = state.expanded ? state.expanded : {}
              if (rowInfo && (rowInfo.viewIndex || rowInfo.viewIndex == 0)) {
                const feat = rowInfo.original as Feature
                if (feat.status != FeatureStatus.Deleted) {
                  if ((expanded as any)[rowInfo.viewIndex]) {
                    setTableInfoExpanded(tableId, {[rowInfo.viewIndex]: {}})
                  } else {
                    setTableInfoExpanded(tableId, {[rowInfo.viewIndex]: {}})
                  }
                }
              }
            }
          },
          style: {
            cursor: 'pointer'
          }
        }
      } else {
        result = {
          style: {
            padding: '3px'
          }
        }
      }
      if (mapService && rowInfo) {
        result.onMouseEnter = () => {
          this.latestFlashFeature = rowInfo.original
          mapService.drawing.mark([this.latestFlashFeature], DrawSource.Select)
        }
        result.onMouseLeave = () => {
          mapService.drawing.unMark(DrawSource.Select, [rowInfo.original])
        }
      }
      return result
    }
  }

  protected panTo(feature: Feature) {
    const mapService = getMapService()
    if (mapService) {
      mapService.panTo(feature.getGeometry())
      mapService.drawing.unMark(DrawSource.Select)
      mapService.drawing.mark([feature], DrawSource.Select)
    }
  }

  protected showPopup(mapDispatcher: IMapDispatcher, feature: Feature, props: ITableProps) {
    const view = getMapService()
    mapDispatcher.setPopupProperties({
      show: false,
      data: [],
      coordinates: [0, 0],
      selectedPage: 0,
      loading: false
    })
    let promise: Promise<void>
    promise = view.panTo(feature.getGeometry())
    promise.then(() => {
      mapDispatcher.setPopupProperties({
        show: true,
        data: [{id: props.id, feature: feature}],
        coordinates: feature.getGeometry().getCenter().getCoordinates() as [number, number],
        selectedPage: 0,
        loading: false
      })
    })
  }
}
